import React from "react";
import moment from "moment";
import ReactMarkdown from "react-markdown";

import { apiUrl } from "../../../../lib/axios-client";
import styles from "./ChatMessage.module.scss";

// Component to parse text and render citation markers
const TextWithCitations = ({ children, citationDataMap, onCitationClick }) => {
  if (typeof children !== "string") {
    // If children is an array or other elements, process each
    if (Array.isArray(children)) {
      return children.map((child, idx) => {
        if (typeof child === "string") {
          return (
            <TextWithCitations
              key={idx}
              citationDataMap={citationDataMap}
              onCitationClick={onCitationClick}
            >
              {child}
            </TextWithCitations>
          );
        }
        return child;
      });
    }
    return children;
  }

  const text = children;
  // Split by citation markers
  const parts = text.split(/(\{\{CITE:[0-9,]+\}\})/g);

  if (parts.length === 1) {
    return text;
  }

  return parts.map((part, idx) => {
    const match = part.match(/\{\{CITE:([0-9,]+)\}\}/);
    if (match) {
      const numbers = match[1].split(",").map(Number);
      const firstNum = numbers[0];
      const citationData = citationDataMap.get(firstNum);

      return (
        <span
          key={idx}
          className={styles.citationMarker}
          onClick={(e) => {
            e.stopPropagation();
            if (citationData && onCitationClick) {
              onCitationClick(citationData);
            }
          }}
          title="Go to source"
        >
          [{numbers.join(", ")}]
        </span>
      );
    }
    return part;
  });
};

const ChatMessage = (props) => {
  const { message, onCitationClick, isLibraryContext } = props;
  const { role, content, citations, created_at } = message;

  // Handle citation click - for library context, open document in new tab
  const handleCitationClick = (citationData) => {
    if (isLibraryContext && citationData.page) {
      // Open document in new tab at the specific page
      const page = citationData.page;
      const url = "/drafts/" + page.document_id + "?page=" + page.page_number;
      window.open(url, "_blank");
    } else if (onCitationClick) {
      // Document context - use the existing handler
      onCitationClick(citationData);
    }
  };

  // Build citation data map - each citation gets its own number (1-indexed)
  // citationDataMap: Map<citationNumber, { page, text }>
  const citationDataMap = new Map();
  if (citations) {
    citations.forEach((citation, idx) => {
      const citationNumber = idx + 1;
      // Use the first page for navigation
      const page = citation.pages && citation.pages[0];
      citationDataMap.set(citationNumber, {
        page: page,
        text: citation.text,
      });
    });
  }

  // Insert citation markers into content at the correct positions
  const getContentWithMarkers = () => {
    if (!citations || citations.length === 0 || !content) {
      return content || "";
    }

    // Sort citations by end position (descending) to insert from end to start
    // This prevents position shifts when inserting markers
    // Include the original index to maintain citation numbering
    const citationsWithIndex = citations.map((c, idx) => ({
      ...c,
      citationNumber: idx + 1,
    }));
    const sortedCitations = [...citationsWithIndex].sort(
      (a, b) => b.end - a.end
    );

    // Group citations by end position
    const markerPositions = new Map();
    sortedCitations.forEach((citation) => {
      const pos = citation.end;
      if (!markerPositions.has(pos)) {
        markerPositions.set(pos, []);
      }
      markerPositions.get(pos).push(citation);
    });

    let result = content;

    // Insert markers from end to start
    const positions = Array.from(markerPositions.keys()).sort((a, b) => b - a);

    positions.forEach((pos) => {
      const citationsAtPos = markerPositions.get(pos);

      // Get citation numbers for this position, sorted
      const citationNumbers = citationsAtPos
        .map((c) => c.citationNumber)
        .sort((a, b) => a - b);

      // Create marker token
      const marker = `{{CITE:${citationNumbers.join(",")}}}`;

      // Insert marker at position
      result = result.slice(0, pos) + marker + result.slice(pos);
    });

    return result;
  };

  // Create wrapper component that injects citation handling
  const createComponentWithCitations = (Tag, className) => {
    return ({ children, ...props }) => (
      <Tag className={className} {...props}>
        <TextWithCitations
          citationDataMap={citationDataMap}
          onCitationClick={handleCitationClick}
        >
          {children}
        </TextWithCitations>
      </Tag>
    );
  };

  // Custom components for react-markdown v6
  const components = {
    p: createComponentWithCitations("p", styles.paragraph),
    li: createComponentWithCitations("li", styles.listItem),
    strong: createComponentWithCitations("strong", styles.strong),
    em: createComponentWithCitations("em", styles.emphasis),
    ul: ({ children }) => <ul className={styles.list}>{children}</ul>,
    ol: ({ children }) => <ol className={styles.list}>{children}</ol>,
    // Headings with constrained sizes
    h1: createComponentWithCitations("h1", `${styles.heading} ${styles.h1}`),
    h2: createComponentWithCitations("h2", `${styles.heading} ${styles.h2}`),
    h3: createComponentWithCitations("h3", `${styles.heading} ${styles.h3}`),
    h4: createComponentWithCitations("h4", `${styles.heading} ${styles.h4}`),
    h5: createComponentWithCitations("h5", `${styles.heading} ${styles.h5}`),
    h6: createComponentWithCitations("h6", `${styles.heading} ${styles.h6}`),
  };

  const contentWithMarkers = getContentWithMarkers();

  // Get unique pages for the Sources section (deduplicated by page id)
  const getUniquePages = () => {
    if (!citations || citations.length === 0) return [];

    const pageMap = new Map();
    citations.forEach((citation) => {
      if (citation.pages) {
        citation.pages.forEach((page) => {
          if (!pageMap.has(page.id)) {
            pageMap.set(page.id, {
              ...page,
              citationText: citation.text, // Store for click handler
            });
          }
        });
      }
    });
    return Array.from(pageMap.values());
  };

  const uniquePages = getUniquePages();

  return (
    <div className={`${styles.message} ${styles[role]}`}>
      <div className={styles.bubble}>
        <div className={styles.content}>
          {role === "assistant" ? (
            <ReactMarkdown components={components}>
              {contentWithMarkers}
            </ReactMarkdown>
          ) : (
            <span>{content}</span>
          )}
        </div>

        {role === "assistant" && uniquePages.length > 0 && (
          <div className={styles.citationsSection}>
            <div className={styles.citationsTitle}>Sources</div>
            <div className={styles.citationsGrid}>
              {uniquePages.map((page, idx) => (
                <div
                  key={page.id}
                  className={styles.citationCard}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCitationClick({
                      page: page,
                      text: page.citationText,
                    });
                  }}
                >
                  <div
                    className={styles.citationThumbnail}
                    style={{
                      backgroundImage:
                        "url('" +
                        apiUrl +
                        "/documents/" +
                        page.document_id +
                        "/page/" +
                        page.page_number +
                        "')",
                    }}
                  />
                  <div className={styles.citationInfo}>
                    <span className={styles.citationPage}>
                      Page {page.page_number}
                    </span>
                    <span className={styles.citationText}>
                      {page.document_title}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles.timestamp}>
        {moment(created_at).format("h:mm A")}
      </div>
    </div>
  );
};

// Loading indicator component for when waiting for response
export const ChatMessageLoading = () => {
  return (
    <div className={styles.loading}>
      <div className={styles.loadingDots}>
        <span></span>
        <span></span>
        <span></span>
      </div>
    </div>
  );
};

export default ChatMessage;
